import { useEffect, useState } from 'react';
import { useTranslations } from 'use-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { userManagementApiClient } from '@/clients/userManagementApiClient';
import { useAuth } from '@/context/AuthContext';
import { useCompany } from '@/hooks/useCompany';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LogoutButton } from '@/components/auth/LogoutButton';
import { InviteCodeForm } from '@/components/InviteCodeForm';
import { userFacingErrorMessage } from '@/utils/userFacingError';

type AcceptState = 'processing' | 'success' | 'error' | 'invalid_email' | 'needs_code' | 'already_registered';

export default function AcceptInvite() {
  const t = useTranslations('consultantAuth.accept');
  const auth = useTranslations('auth.invite');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, session, signOut } = useAuth();
  const { refetch: refetchCompany } = useCompany();
  const [state, setState] = useState<AcceptState>('processing');
  const [error, setError] = useState<string | null>(null);
  const [lookupInfo, setLookupInfo] = useState<{ email: string; companyName: string } | null>(null);
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setState('error');
      setError(t('noToken'));
      return;
    }

    // No session yet — look up the invitation and let the invitee type the
    // code we emailed them. verifyOtp establishes the session directly, so
    // there is no need to redirect through /auth first.
    if (!session) {
      let cancelled = false;
      (async () => {
        const result = await userManagementApiClient.lookupInvitation(token);
        if (cancelled) return;
        if (!result.success || !result.data) {
          if (result.error === 'already_registered') {
            setState('already_registered');
            return;
          }
          setState('error');
          setError(result.error || t('invalidOrExpired'));
          return;
        }
        setLookupInfo(result.data);
        setState('needs_code');
      })();
      return () => {
        cancelled = true;
      };
    }

    if (user?.email) {
      acceptInvitation();
    }
  }, [token, user, session]);

  const acceptInvitation = async () => {
    if (!token || !user?.email) return;

    setState('processing');
    try {
      const result = await userManagementApiClient.acceptInvitation(token);

      if (result.success && result.data) {
        // Refetch company data to get the newly joined company
        await refetchCompany();
        setState('success');

        // Redirect to overview after a short delay
        setTimeout(() => {
          navigate('/overview');
        }, 2000);
      } else {
        // Check if it's an email mismatch error
        if (result.error?.toLowerCase().includes('different email')) {
          setState('invalid_email');
          setError(result.error);
        } else {
          setState('error');
          setError(result.error || t('acceptFailed'));
        }
      }
    } catch (err) {
      setState('error');
      setError(userFacingErrorMessage(err, t('unexpected')));
    }
  };

  const handleSignOut = async () => {
    await signOut();
    // Go straight back to accept-invite — no /auth round trip needed, since
    // this page's own effect already handles the no-session case (code entry).
    navigate(`/accept-invite?token=${token}`, { replace: true });
  };

  if (state === 'needs_code' && lookupInfo && token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{auth('codeTitle')}</CardTitle>
            <CardDescription>{lookupInfo.companyName}</CardDescription>
          </CardHeader>
          <CardContent>
            <InviteCodeForm
              token={token}
              email={lookupInfo.email}
              companyName={lookupInfo.companyName}
              onVerified={() => setState('processing')}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === 'processing') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        {user && <LogoutButton />}
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t('processingTitle')}</CardTitle>
            <CardDescription>{t('processingDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center py-8">
            <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
            <p className="text-sm text-muted-foreground">{t('joining')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        {user && <LogoutButton />}
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-6 w-6 text-green-500" />
              {t('successTitle')}
            </CardTitle>
            <CardDescription>
              {t('successDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                {t('successBody')}
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === 'already_registered') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{auth('alreadyRegisteredTitle')}</CardTitle>
            <CardDescription>{auth('alreadyRegisteredDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertDescription>
                {auth('alreadyRegisteredBody')}
              </AlertDescription>
            </Alert>
            <Button
              onClick={() => navigate(`/auth?returnUrl=${encodeURIComponent('/overview')}`)}
              className="w-full"
            >
              {auth('logIn')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === 'invalid_email') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        {user && <LogoutButton />}
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <XCircle className="h-6 w-6 text-destructive" />
              {t('invalidTitle')}
            </CardTitle>
            <CardDescription>
              {t('invalidDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>

            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                {t('invalidHint')}
              </p>
              <p className="text-sm text-muted-foreground">
                {t.rich('signedInAs', { email: user?.email ?? '', strong: (chunks) => <strong>{chunks}</strong> })}
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Button onClick={handleSignOut} variant="outline" className="w-full">
                {auth('signOutAndRetry')}
              </Button>
              <Button onClick={() => navigate('/overview')} variant="ghost" className="w-full">
                {auth('goToDashboard')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      {user && <LogoutButton />}
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <XCircle className="h-6 w-6 text-destructive" />
            {auth('errorTitle')}
          </CardTitle>
          <CardDescription>
            {t('problemDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert variant="destructive">
            <AlertDescription>{error || t('invalidOrExpired')}</AlertDescription>
          </Alert>

          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              {t('problemHint')}
            </p>
          </div>

          <Button onClick={() => navigate('/overview')} className="w-full">
            {auth('goToDashboard')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
